<template>
    <div @click="onClick" style="cursor: pointer;">
        <span  class="ag-menu-option-part ag-menu-option-icon w-[30px] h-[34px]" data-ref="eIcon" role="presentation">
            <BxCheck v-if="isChecked" class="text-white text-lg available" />
        </span>
        <span :data-testid="params.dataTestId" class="ag-menu-option-part ag-menu-option-text" data-ref="eName">{{ params.name }}</span>

        <span class="ag-menu-option-part ag-menu-option-shortcut" data-ref="eShortcut"></span>

        <span class="ag-menu-option-part ag-menu-option-popup-pointer">
            <span v-if="params.subMenu" class="ag-icon ag-icon-small-right" unselectable="on"
                role="presentation"></span>
        </span>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onActivated, onMounted } from 'vue';
import BxCheck from '~/assets/svgs/BxCheck.vue';

interface MenuItemParams {
    name: string;
    initialChecked?: boolean;
    getChecked?: () => boolean; // New: getter function for dynamic value
    onToggle?: (checked: boolean) => void;
    subMenu?: boolean;
    dataTestId?: string;
}

interface Props {
    params: MenuItemParams;
}

const props = defineProps<Props>();

const isChecked = ref(false);

// Function to sync the checked state from the source
const syncCheckedState = () => {
    if (props.params.getChecked) {
        // If getter function is provided, use it for dynamic value
        isChecked.value = props.params.getChecked();
    } else if (props.params.initialChecked !== undefined) {
        // Fallback to initialChecked for backward compatibility
        isChecked.value = props.params.initialChecked;
    }
};

// Sync on mount
onMounted(() => {
    syncCheckedState();
});

// Sync when component is activated (shown in menu)
// This handles the case when hovering between menu items
onActivated(() => {
    syncCheckedState();
});

// Watch for changes in initialChecked prop (in case it's reactive)
watch(
    () => props.params.initialChecked,
    (newValue) => {
        if (newValue !== undefined && !props.params.getChecked) {
            isChecked.value = newValue;
        }
    }
);

// Watch for the getter function and re-sync when params change
watch(
    () => props.params.getChecked,
    () => {
        syncCheckedState();
    }
);

const onClick = () => {
    // Handle toggle mode
    if (props.params.onToggle) {
        // Sync first to ensure we have the latest state
        syncCheckedState();
        isChecked.value = !isChecked.value;
        props.params.onToggle(isChecked.value);
    }
};

const configureDefaults = () => {
    return true;
};

// Expose methods if needed by parent components
defineExpose({
    configureDefaults,
});
</script>
